import React, { useMemo, useCallback, useEffect, useState } from 'react';
import {
  Box, CircularProgress, Alert, useTheme, useMediaQuery,
  Dialog, DialogTitle, DialogContent, Typography, DialogActions, Button,
  Stack
} from '@mui/material';
import { Calendar, ToolbarProps, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import { localizer } from './utils/localizer';
import { useAppointments } from './hooks/useAppointments';
import { useAvailableSlots } from './hooks/useAvailableSlots';
import { useBookingConfig } from '../BookingConfiguration/hooks/useBookingConfig';
import { parseDaysInfoFromApi } from '../BookingConfiguration/utils';
import { RBCEvent } from './types';
import { getCalendarStyles } from './styles/calendar.styles';
import { useCalendarLogic } from './hooks/useCalendarLogic';
import {
  AppointmentModal,
  CalendarHeader,
  CustomToolbar,
  CustomDateHeader,
  EventComponent
} from '.';
import { SlotCacheProvider } from './context/SlotCacheContext';
import { CALENDAR_VIEWS, CALENDAR_FORMATS, NON_BOOKABLE_CELL_COLORS } from './constants';
import { getCalendarRange } from './utils/calendarRange';

const DnDCalendar = withDragAndDrop(Calendar as any) as any;

// ─── Static time grid ──────────────────────────────────────────────────────
// The grid spans a full day (12:00 AM → 12:00 AM next day) with 1-hour labeled
// rows subdivided into 15-min cells (so sub-hour API slots color precisely).
const GRID_MIN_TIME = new Date(1972, 0, 1, 0, 0, 0, 0);
const GRID_MAX_TIME = new Date(1972, 0, 1, 23, 59, 59, 999);
const GRID_SCROLL_TO_TIME = new Date(1972, 0, 1, 9, 0, 0, 0);
const GRID_STEP_MINUTES = 15;
const GRID_TIMESLOTS = 4; // 4 × 15min = 1-hour rows
// Pad the available-slots window a few hours on BOTH ends so overnight slots at
// either edge of the visible range are covered.
const SLOT_WINDOW_BUFFER_MS = 4 * 60 * 60 * 1000;

interface CalendarViewProps {
  smbId: string;
}

export const CalendarView: React.FC<CalendarViewProps> = ({ smbId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Data Hooks
  const { config: rawConfig, fetchConfig } = useBookingConfig(smbId);

  // Parse days_info.slots from API string[][] format to form TimeRange[] format
  const config = useMemo(() => {
    if (!rawConfig?.days_info) return rawConfig;
    return {
      ...rawConfig,
      days_info: {
        slots: parseDaysInfoFromApi(rawConfig.days_info),
      },
    };
  }, [rawConfig]);
  const {
    appointments,
    isLoading,
    error,
    fetchAppointments,
    fetchAgendaAppointments,
    createAppointment,
    updateAppointment,
    refetchCurrentRange,
    total
  } = useAppointments(smbId, config?.timezone);

  // Listen for banner refresh event to refetch in-place cleanly without re-mounting
  useEffect(() => {
    const handleRefreshEvent = () => {
      refetchCurrentRange();
    };
    window.addEventListener('refresh-appointments', handleRefreshEvent);
    return () => window.removeEventListener('refresh-appointments', handleRefreshEvent);
  }, [refetchCurrentRange]);

  // Available (bookable) slots come from the backend — the frontend no longer
  // computes availability from config.
  const { availableSlots, isLoadingSlots, fetchAvailableSlots } = useAvailableSlots(smbId, config?.timezone);

  // Bumped after any successful mutation so the white-slot grid refetches
  // (a booking/reschedule consumes a slot).
  const [slotRefreshKey, setSlotRefreshKey] = useState(0);
  const refreshSlots = useCallback(() => setSlotRefreshKey(k => k + 1), []);

  // updateAppointment used for drag-and-drop reschedule must also refresh slots.
  const updateAppointmentWithRefresh = useCallback(async (id: string, payload: any) => {
    const ok = await updateAppointment(id, payload);
    if (ok) refreshSlots();
    return ok;
  }, [updateAppointment, refreshSlots]);

  // ─── Timezone-aware "now" ──────────────────────────────────────────────
  // The browser's JS timezone may differ from the business timezone (e.g., VM in UTC, business in IST).
  // We compute a Date whose local-time digits match the wall-clock time in the configured timezone.
  const getNow = useCallback(() => {
    if (!config?.timezone) return new Date(); // Fallback to system time until config loads

    const tz = config.timezone;
    try {
      const now = new Date();
      // Use en-GB to predictably get 24-hour format and avoid weird en-US hour 24
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      }).formatToParts(now);

      const get = (type: string) => parts.find(p => p.type === type)?.value || '0';

      const year = parseInt(get('year'));
      const month = parseInt(get('month')) - 1;
      const day = parseInt(get('day'));
      let hour = parseInt(get('hour'));
      const minute = parseInt(get('minute'));
      const second = parseInt(get('second'));

      // Just in case some browsers return 24 instead of 00
      if (hour === 24) hour = 0;

      return new Date(year, month, day, hour, minute, second);
    } catch {
      return new Date();
    }
  }, [config?.timezone]);

  // Layout & Interaction Logic Hook
  const {
    view, setView,
    date,
    modalOpen, setModalOpen,
    selectedEvent,
    searchTerm, setSearchTerm,
    agendaLength,
    agendaPage,
    agendaLimit,
    events,
    isWorkingDay,
    availableCellLookup,
    handleNavigate,
    handleDateRangeApply,
    handleAgendaPageChange,
    handleSelectSlot,
    handleSelectEvent,
    handleEventDrop,
    dndConfirmState,
    handleConfirmDnd,
    handleCancelDnd,
    getAgendaRange
  } = useCalendarLogic(appointments, config, updateAppointmentWithRefresh, availableSlots, getNow);

  const onSelecting = useCallback(() => false, []);

  // ─── Optimistic loading state for pagination ───────────────────────────
  // This triggers immediately on button click, before the API call starts
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  const handlePageChange = useCallback(async (newPage: number) => {
    setIsPaginationLoading(true);
    handleAgendaPageChange(newPage);
  }, [handleAgendaPageChange]);

  // Sync pagination loading with global loading state
  useEffect(() => {
    if (!isLoading) {
      setIsPaginationLoading(false);
    }
  }, [isLoading]);



  /**
   * dayPropGetter — controls the COLUMN (and All-Day row) background.
   *   • Non-working day  → red
   *   • Past working day → grey
   *   • Current / future → no bg (lets .rbc-today CSS blue show through)
   */
  const dayPropGetter = useCallback((date: Date) => {
    if (!isWorkingDay(date)) {
      return { className: 'rbc-non-working-day', style: { backgroundColor: NON_BOOKABLE_CELL_COLORS.RED } };
    }
    const now = getNow();
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    if (endOfDay < now) {
      return { className: 'rbc-past-day', style: { backgroundColor: NON_BOOKABLE_CELL_COLORS.GREY } };
    }
    return {};
  }, [isWorkingDay, getNow]);

  /**
   * slotPropGetter — controls each individual time-cell background.
   *
   *  Rule 1 – Non-working day → transparent, let red day bg show.
   *  Rule 2 – Past cell: all slots whose END is at-or-before now → grey.
   *  Rule 3 – Outside this day's working-hours ranges → grey.
   *  Rule 4 – Bookable future cell: white (#FFFFFF).
   */
  const CELL_SIZE = 15;
  const slotPropGetter = useCallback((date: Date) => {
    if (!isWorkingDay(date)) return {};

    const now = getNow();
    const slotEnd = new Date(date.getTime() + CELL_SIZE * 60 * 1000);

    if (slotEnd <= now) {
      return { className: 'rbc-past-slot', style: { backgroundColor: NON_BOOKABLE_CELL_COLORS.GREY } };
    }

    if (!availableCellLookup.has(`${date.getTime()}-${slotEnd.getTime()}`)) {
      return { className: 'rbc-non-working-slot', style: { backgroundColor: NON_BOOKABLE_CELL_COLORS.GREY } };
    }

    return { style: { backgroundColor: '#FFFFFF' } };
  }, [isWorkingDay, getNow, availableCellLookup]);

  // ─── Fetch Booking Config (one-time per SMB) ──────────────────────────
  useEffect(() => {
    fetchConfig();
  }, [smbId, fetchConfig]);

  // ─── Fetch Appointments ────────────────────────────────────────────────
  // Deps: view/date/pagination cause re-fetches. fetchAppointments/fetchAgendaAppointments
  // recreate when `timezone` changes (config load), which transitively triggers a refetch.
  // config?.duration is intentionally NOT a dep — it caused a redundant extra fetch on load.
  // Buffer is fixed at 2 h to cover any realistic slot duration without adding a dep.
  useEffect(() => {
    const BUFFER_MS = 2 * 60 * 60 * 1000; // 2 h — covers any slot duration

    if (view === Views.AGENDA) {
      const { start, end } = getAgendaRange(date, agendaLength);
      fetchAgendaAppointments(start, end, agendaPage, agendaLimit);
    } else {
      const { start, end } = getCalendarRange(view, date);
      fetchAppointments(
        new Date(start.getTime() - BUFFER_MS),
        new Date(end.getTime() + BUFFER_MS),
      );
    }

    // Available slots drive the white-cell coloring, which only the time-grid
    // views (week/day) render. Pad both ends by a few hours so overnight slots
    // at either edge of the visible window are covered.
    if (view === Views.WEEK || view === Views.DAY) {
      const { start, end } = getCalendarRange(view, date);
      fetchAvailableSlots(
        new Date(start.getTime() - SLOT_WINDOW_BUFFER_MS),
        new Date(end.getTime() + SLOT_WINDOW_BUFFER_MS),
      );
    }
  }, [view, date, agendaLength, agendaPage, agendaLimit, slotRefreshKey, fetchAppointments, fetchAgendaAppointments, fetchAvailableSlots, getAgendaRange]);



  const eventPropGetter = useCallback((event: RBCEvent) => {
    const isAgenda = view === 'agenda';
    let color = theme.palette.primary.main;
    let darkColor = theme.palette.primary.dark;

    if (event.status === 'CANCELLED') {
      color = theme.palette.error.main;
      darkColor = theme.palette.error.dark;
    } else if (event.status === 'COMPLETED') {
      color = theme.palette.success.main;
      darkColor = theme.palette.success.dark;
    }

    // Determine if the event is currently happening or in un-bookable time
    const now = getNow();
    const eventStart = event.start ?? new Date(0);
    const eventEnd = event.end ?? eventStart;

    // Event is currently happening if 'now' falls within its time range (contains the red current time line)
    const isCurrentlyHappening = eventStart <= now && now < eventEnd;
    // Event is fully in the past (ended before now)
    const isFullyPast = eventEnd < now;

    // Past appointments are dimmed; currently-happening events stay highlighted.
    // (Working-hours-based dimming was removed — availability is now API-driven.)
    const isUnbookable = isFullyPast && !isCurrentlyHappening;

    if (isAgenda) {
      return {
        style: {
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          borderLeft: `4px solid ${color}`,
          borderRadius: 0,
          fontWeight: 500,
        }
      };
    }

    if (isUnbookable) {
      return {
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.06)',
          borderLeft: `4px solid ${darkColor}`,
          color: theme.palette.text.secondary,
          borderRadius: '4px',
          opacity: event.status === 'CANCELLED' ? 0.5 : 0.75,
          fontWeight: 600,
          fontSize: '0.75rem',
          textDecoration: event.status === 'CANCELLED' ? 'line-through' : 'none',
        }
      };
    }

    return {
      style: {
        backgroundColor: color,
        borderLeft: `4px solid ${darkColor}`,
        color: '#fff',
        borderRadius: '4px',
        opacity: event.status === 'CANCELLED' ? 0.7 : 1,
        fontWeight: 600,
        fontSize: '0.75rem',
        textDecoration: event.status === 'CANCELLED' ? 'line-through' : 'none',
      }
    };
  }, [theme, view, getNow]);
  if (!smbId) {
    return <Alert severity="info">Please select an SMB to configure bookings.</Alert>;
  }

  const calendarStyles = getCalendarStyles(theme, isMobile);

  return (
    <SlotCacheProvider
      availableSlots={availableSlots}
      isLoadingSlots={isLoadingSlots}
      fetchAvailableSlots={fetchAvailableSlots}
    >
      <Box sx={calendarStyles}>
      {/* Fixed height slot to preserve layout space (0 CLS) while providing visual gap around loading bar */}

        <CalendarHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          eventCount={events.length}
        />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2, flexShrink: 0 }}>{error}</Alert>}

          {/* Calendar container - agenda handles its own scroll, calendar views fill space */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            opacity: (isLoading && !isPaginationLoading) ? 0.6 : 1,
            transition: 'opacity 0.2s ease-in-out',
            pointerEvents: (isLoading && !isPaginationLoading) ? 'none' : 'auto',
          }}>
            <DnDCalendar
              localizer={localizer}
              events={events}
              style={{ flex: 1 }}
              startAccessor="start"
              endAccessor="end"
              view={view}
              views={CALENDAR_VIEWS}
              onView={setView}
              date={date}
              onNavigate={handleNavigate}
              selectable
              onSelecting={onSelecting}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onEventDrop={handleEventDrop}
              resizable={false}
              min={GRID_MIN_TIME}
              max={GRID_MAX_TIME}
              getNow={getNow}
              scrollToTime={GRID_SCROLL_TO_TIME}
              formats={CALENDAR_FORMATS}
              eventPropGetter={eventPropGetter}
              dayPropGetter={dayPropGetter}
              slotPropGetter={slotPropGetter}
              step={GRID_STEP_MINUTES}
              timeslots={GRID_TIMESLOTS}
              dayLayoutAlgorithm="no-overlap"
              showMultiDayTimes
              length={agendaLength}
              components={{
                toolbar: (tbProps: ToolbarProps<RBCEvent>) => (
                  <CustomToolbar {...tbProps} onDateRangeApply={handleDateRangeApply} currentDate={date} currentAgendaLength={agendaLength} isLoading={isLoading} />
                ),
                timeGutterHeader: () => (
                  <Box sx={{
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '100%',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    <Box sx={{ height: '60%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0' }}>
                      Days/<br />Slots
                    </Box>
                    <Box sx={{ height: '38%', 'textWrap': 'nowrap', display: 'flex', justifyContent: 'center', alignItems: 'center', borderTop: '2px solid rgb(0, 0, 0, 0.2)' }}>
                      All Day
                    </Box>
                  </Box>
                ),
                week: {
                  header: (props: any) => <CustomDateHeader {...props} isWorkingDay={isWorkingDay(props.date)} />
                },
                day: {
                  header: (props: any) => <CustomDateHeader {...props} isWorkingDay={isWorkingDay(props.date)} />
                },
                event: (props: any) => <EventComponent {...props} isAgenda={view === Views.AGENDA} disableTooltip={view === Views.AGENDA} />,
              }}
            />
          </Box>

          {/* Agenda View Pagination Controls */}
          {view === Views.AGENDA && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderTop: '1px solid rgba(224, 224, 224, 1)', bgcolor: 'background.paper', flexShrink: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {total > 0 ? (
                  <>
                    Showing {((agendaPage - 1) * agendaLimit) + 1}-{Math.min(agendaPage * agendaLimit, total)} of {total} appointments
                  </>
                ) : (
                  'No appointments found'
                )}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  disabled={agendaPage === 1 || isPaginationLoading}
                  onClick={() => handlePageChange(agendaPage - 1)}
                  sx={{ minWidth: 60, px: 1, fontSize: '0.75rem', height: 28 }}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={agendaPage * agendaLimit >= total || isPaginationLoading}
                  onClick={() => handlePageChange(agendaPage + 1)}
                  sx={{ minWidth: 60, px: 1, fontSize: '0.75rem', height: 28 }}
                >
                  Next
                </Button>
                {/* Pagination loading overlay - instant feedback */}
                {isPaginationLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <CircularProgress size={16} thickness={5} sx={{ color: 'primary.main' }} />
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Box>

        <AppointmentModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          smbId={smbId}
          selectedEvent={selectedEvent}
          onSaveCreate={async (payload) => {
            const success = await createAppointment(payload);
            if (success) {
              setSearchTerm(''); // Clear search so new appointment is visible
              refreshSlots(); // A booking consumes a slot — refresh the white grid
            }
            return success;
          }}
          onSaveUpdate={async (id, payload) => {
            const success = await updateAppointment(id, payload);
            if (success) {
              setSearchTerm(''); // Clear search so updated appointment is visible
              refreshSlots();
            }
            return success;
          }}
          getNow={getNow}
        />

        {/* Drag & Drop Confirmation Dialog */}
        {dndConfirmState && (
          <Dialog open={dndConfirmState.open} onClose={handleCancelDnd} maxWidth="xs" fullWidth>
            <DialogTitle>Confirm Reschedule</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to reschedule this appointment to the selected time?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
              <Button onClick={handleCancelDnd} color="inherit">Cancel</Button>
              <Button
                onClick={handleConfirmDnd}
                color="primary"
                variant="contained"
              >
                Confirm Reschedule
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </SlotCacheProvider>
  );
};
